import { useDispatch, useSelector } from 'react-redux'
import { RegModalWrapper } from '../ModalContent'
import {
    Box,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Typography,
    useTheme,
} from '@mui/material'
import styles from '../index.module.scss'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { setErrorMsg } from '../../../../redux/uiSlice'
import z from 'zod'
import { selectUserUid } from '../../../../redux/userSlice'
import {
    selectDerivedTeacherEmploymentSetting,
    selectTeacherReference,
    selectTeacherWorkingHistory,
} from '../../../../redux/teacherSlice'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DayjsSchema } from '../../../../schemas/helper'
import dayjs from '../../../../helpers/dayjs'
import FormInputText_v2 from '../../../../components/FormComponents/FormInputText_v2'
import { TeacherReference } from '../../../../schemas/teacherReg'
import { TEACHER_API } from '../../../../firebase/teacherApi'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { UserRegSx } from '../../../../components/TextField'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'
import { nonEmptyStringSchema } from '../../../RefereeSurveyPage/components/ReferenceSurvey_v3'
import { logger } from '../../../../helpers/logger'

const formTypes = z.enum(['interview', 'other', 'selectedHistory'] as const)
type ReferenceFormTypes = z.infer<typeof formTypes>

const baseForm = z.object({
    whenWeLastWorkedTogether: DayjsSchema.refine(
        (v) => v.isBefore(dayjs()),
        'Please select a date before today'
    ).nullable(),
    whereWeWorkedTogether: nonEmptyStringSchema,
    workingRelationship: nonEmptyStringSchema,

    firstName: nonEmptyStringSchema,
    lastName: nonEmptyStringSchema,
    email: z.email(),
})

const interviewSchema = z.object({
    type: formTypes.extract(['interview']),
    data: z.object().optional(),
})
const selectedHistorySchema = z.object({
    type: formTypes.extract(['selectedHistory']),
    data: baseForm
        .omit({
            whereWeWorkedTogether: true,
        })
        .extend({
            workplaceUid: z.string(),
        })
        .refine(
            (v) =>
                v.whenWeLastWorkedTogether !== null &&
                v.whenWeLastWorkedTogether.isValid(),
            'Please select a valid date'
        ),
})
const otherSchema = z.object({
    type: formTypes.extract(['other']),
    data: baseForm.refine(
        (v) =>
            v.whenWeLastWorkedTogether !== null &&
            v.whenWeLastWorkedTogether.isValid(),
        'Please select a valid date'
    ),
})

const formSchema = z.discriminatedUnion('type', [
    interviewSchema,
    otherSchema,
    selectedHistorySchema,
])
type FormType = z.infer<typeof formSchema>

const Reference = () => {
    const dispatch = useDispatch()

    const userId = useSelector(selectUserUid)
    const workingHistory = useSelector(selectTeacherWorkingHistory)
    const reference = useSelector(selectTeacherReference)
    const employmentSettingType = useSelector(
        selectDerivedTeacherEmploymentSetting
    )
    const theme = useTheme()

    const {
        reset,
        watch,
        control,
        handleSubmit,
        formState: { isValid, isDirty },
        setValue,
        resetField,
    } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    })
    const formType = watch('type')

    const [hasPartialData, setHasPartialData] = useState(false)
    const defaultWorkplaceUid = useRef<string>()
    const hasAppliedDefault = useRef(false)
    useEffect(() => {
        if (hasAppliedDefault.current || reference.status !== 'success') return

        hasAppliedDefault.current = true

        const isPartiallyComplete =
            typeof reference.data.firstName === 'string' ||
            typeof reference.data.lastName === 'string' ||
            typeof reference.data.email === 'string' ||
            typeof reference.data.whenWeLastWorkedTogether === 'string' ||
            typeof reference.data.whereWeWorkedTogether === 'string' ||
            typeof reference.data.workingRelationship === 'string'

        let defaultForm: FormType | undefined = undefined
        if (Boolean(reference.data.requestedInterview)) {
            defaultForm = {
                type: 'interview',
            }
        } else if (typeof reference.data.workplaceUid === 'string') {
            defaultWorkplaceUid.current = reference.data.workplaceUid
            defaultForm = {
                type: 'selectedHistory',
                data: {
                    whenWeLastWorkedTogether:
                        typeof reference.data.whenWeLastWorkedTogether ===
                        'string'
                            ? dayjs(
                                  reference.data.whenWeLastWorkedTogether,
                                  'MM-YYYY'
                              )
                            : null,
                    workingRelationship:
                        reference.data.workingRelationship || '',
                    firstName: reference.data.firstName || '',
                    lastName: reference.data.lastName || '',
                    email: reference.data.email || '',
                    workplaceUid: reference.data.workplaceUid,
                },
            }
        } else if (isPartiallyComplete) {
            defaultForm = {
                type: 'other',
                data: {
                    whenWeLastWorkedTogether:
                        typeof reference.data.whenWeLastWorkedTogether ===
                        'string'
                            ? dayjs(
                                  reference.data.whenWeLastWorkedTogether,
                                  'MM-YYYY'
                              )
                            : null,
                    whereWeWorkedTogether:
                        reference.data.whereWeWorkedTogether || '',
                    workingRelationship:
                        reference.data.workingRelationship || '',
                    firstName: reference.data.firstName || '',
                    lastName: reference.data.lastName || '',
                    email: reference.data.email || '',
                },
            }
            setHasPartialData(
                typeof reference.data.firstName === 'string' ||
                    typeof reference.data.lastName === 'string' ||
                    typeof reference.data.email === 'string' ||
                    reference.data.state !== 'incomplete'
            )
        }

        if (defaultForm) {
            reset(defaultForm)
        }
    }, [reference, reset])

    const onSubmit = async ({ type, data }: FormType) => {
        try {
            if (!isDirty) return
            if (!userId) throw new Error('User is not authenticated.')
            let toUpdate: Partial<TeacherReference>

            switch (type) {
                case 'selectedHistory':
                case 'other': {
                    toUpdate = {
                        ...data,
                        whenWeLastWorkedTogether:
                            data.whenWeLastWorkedTogether!.format('MM-YYYY'),
                        state: 'submitted',
                    }
                    break
                }
                case 'interview': {
                    toUpdate = {
                        requestedInterview: true,
                        state: 'submitted',
                    }
                    break
                }
            }

            await TEACHER_API.setChild(userId, 'reference', toUpdate)
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    const typeOptions: // If formType if  Value represents the
        | { label: ReactNode; value: ReferenceFormTypes; id?: string }[]
        | undefined = useMemo(() => {
        if (workingHistory.status !== 'success') return undefined

        const historyOptions = Object.entries(
            workingHistory.data.entries || {}
        ).map(
            ([k, v]): {
                label: ReactNode
                value: ReferenceFormTypes
                id: string
            } => {
                return {
                    label: (
                        <Stack>
                            <Typography>{v.supervisorName}</Typography>
                            <Typography
                                variant="ps"
                                sx={{ color: styles.neutrals500 }}
                            >
                                {v.employer}
                            </Typography>
                        </Stack>
                    ),
                    value: 'selectedHistory',
                    // formType
                    id: k,
                }
            }
        )

        return [
            ...historyOptions,
            { label: 'Request Online Interview', value: 'interview' },
            { label: 'Nominate someone else', value: 'other' },
        ]
    }, [workingHistory])

    const hideForm = !hasPartialData && workingHistory.data?.entryCount === 0

    const renderForm = (formType: ReferenceFormTypes) => {
        // Condition to hide/give empty state to complete working history first

        switch (formType) {
            case 'interview': {
                return (
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: styles.neutrals500,
                        }}
                        variant="ps"
                    >
                        You will be contacted by Spark to arrange a suitable
                        time for an online interview once your application has
                        been submitted
                    </Typography>
                )
            }
            case 'selectedHistory':
            case 'other': {
                return (
                    <Stack sx={{ gap: 3 }}>
                        {formType === 'other' ? (
                            <FormInputText_v2
                                control={control}
                                name={'data.whereWeWorkedTogether'}
                                formLabel={
                                    'Where did you work with this person?'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                    placeholder: 'Company or organisation',
                                    slotProps: {
                                        input: {
                                            startAdornment:
                                                // prettier-ignore
                                                <InputAdornment position='start'><svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M14 20.0176C14.3867 20.0176 14.9229 18.5498 14.9229 16.1768V10.6396C16.4082 10.2354 17.4893 8.87305 17.4893 7.26465C17.4893 5.33105 15.9424 3.75781 14 3.75781C12.0664 3.75781 10.5195 5.33105 10.5195 7.26465C10.5195 8.86426 11.5918 10.2266 13.0771 10.6309V16.1768C13.0771 18.541 13.6221 20.0176 14 20.0176ZM13.0156 7.45801C12.3828 7.45801 11.8203 6.9043 11.8203 6.25391C11.8203 5.6123 12.3828 5.06738 13.0156 5.06738C13.6748 5.06738 14.2109 5.6123 14.2109 6.25391C14.2109 6.9043 13.6748 7.45801 13.0156 7.45801ZM14 23.4805C19.5898 23.4805 22.9385 21.5996 22.9385 19.4375C22.9385 17.0732 19.3174 15.5439 16.4258 15.4209V16.9941C18.3945 17.082 20.9346 17.9873 20.9346 19.2617C20.9346 20.7734 18.0166 21.8193 14 21.8193C9.9834 21.8193 7.07422 20.7822 7.07422 19.2617C7.07422 17.9873 9.61426 17.082 11.5742 16.9941V15.4209C8.68262 15.5439 5.06152 17.0732 5.06152 19.4375C5.06152 21.5996 8.41016 23.4805 14 23.4805Z" fill="#555555"/> </svg> </InputAdornment>,
                                        },
                                    },
                                }}
                            />
                        ) : null}
                        <FormDatePicker_v2
                            control={control}
                            name={'data.whenWeLastWorkedTogether'}
                            datePickerProps={{
                                format: 'MM YYYY',
                                views: ['year', 'month'],
                                maxDate: dayjs(),
                                sx: UserRegSx,
                                slots: {
                                    openPickerIcon: () =>
                                        // prettier-ignore
                                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M7.94434 21.8809H20.0469C21.9541 21.8809 22.9473 20.8965 22.9473 19.0156V8.31934C22.9473 6.43848 21.9541 5.4541 20.0469 5.4541H7.94434C6.0459 5.4541 5.05273 6.42969 5.05273 8.31934V19.0156C5.05273 20.8965 6.0459 21.8809 7.94434 21.8809ZM7.93555 20.1318C7.20605 20.1318 6.80176 19.7539 6.80176 18.9805V10.8945C6.80176 10.1211 7.20605 9.74316 7.93555 9.74316H20.0557C20.7852 9.74316 21.1895 10.1211 21.1895 10.8945V18.9805C21.1895 19.7539 20.7852 20.1318 20.0557 20.1318H7.93555ZM12.3037 12.7754H12.8223C13.1387 12.7754 13.2441 12.6787 13.2441 12.3711V11.8525C13.2441 11.5361 13.1387 11.4395 12.8223 11.4395H12.3037C11.9873 11.4395 11.8818 11.5361 11.8818 11.8525V12.3711C11.8818 12.6787 11.9873 12.7754 12.3037 12.7754ZM15.1777 12.7754H15.6963C16.0039 12.7754 16.1094 12.6787 16.1094 12.3711V11.8525C16.1094 11.5361 16.0039 11.4395 15.6963 11.4395H15.1777C14.8613 11.4395 14.7559 11.5361 14.7559 11.8525V12.3711C14.7559 12.6787 14.8613 12.7754 15.1777 12.7754ZM18.043 12.7754H18.5615C18.8779 12.7754 18.9834 12.6787 18.9834 12.3711V11.8525C18.9834 11.5361 18.8779 11.4395 18.5615 11.4395H18.043C17.7354 11.4395 17.6299 11.5361 17.6299 11.8525V12.3711C17.6299 12.6787 17.7354 12.7754 18.043 12.7754ZM9.43848 15.6055H9.94824C10.2646 15.6055 10.3701 15.5088 10.3701 15.1924V14.6738C10.3701 14.3662 10.2646 14.2695 9.94824 14.2695H9.43848C9.12207 14.2695 9.0166 14.3662 9.0166 14.6738V15.1924C9.0166 15.5088 9.12207 15.6055 9.43848 15.6055ZM12.3037 15.6055H12.8223C13.1387 15.6055 13.2441 15.5088 13.2441 15.1924V14.6738C13.2441 14.3662 13.1387 14.2695 12.8223 14.2695H12.3037C11.9873 14.2695 11.8818 14.3662 11.8818 14.6738V15.1924C11.8818 15.5088 11.9873 15.6055 12.3037 15.6055ZM15.1777 15.6055H15.6963C16.0039 15.6055 16.1094 15.5088 16.1094 15.1924V14.6738C16.1094 14.3662 16.0039 14.2695 15.6963 14.2695H15.1777C14.8613 14.2695 14.7559 14.3662 14.7559 14.6738V15.1924C14.7559 15.5088 14.8613 15.6055 15.1777 15.6055ZM18.043 15.6055H18.5615C18.8779 15.6055 18.9834 15.5088 18.9834 15.1924V14.6738C18.9834 14.3662 18.8779 14.2695 18.5615 14.2695H18.043C17.7354 14.2695 17.6299 14.3662 17.6299 14.6738V15.1924C17.6299 15.5088 17.7354 15.6055 18.043 15.6055ZM9.43848 18.4268H9.94824C10.2646 18.4268 10.3701 18.3301 10.3701 18.0225V17.5039C10.3701 17.1875 10.2646 17.0908 9.94824 17.0908H9.43848C9.12207 17.0908 9.0166 17.1875 9.0166 17.5039V18.0225C9.0166 18.3301 9.12207 18.4268 9.43848 18.4268ZM12.3037 18.4268H12.8223C13.1387 18.4268 13.2441 18.3301 13.2441 18.0225V17.5039C13.2441 17.1875 13.1387 17.0908 12.8223 17.0908H12.3037C11.9873 17.0908 11.8818 17.1875 11.8818 17.5039V18.0225C11.8818 18.3301 11.9873 18.4268 12.3037 18.4268ZM15.1777 18.4268H15.6963C16.0039 18.4268 16.1094 18.3301 16.1094 18.0225V17.5039C16.1094 17.1875 16.0039 17.0908 15.6963 17.0908H15.1777C14.8613 17.0908 14.7559 17.1875 14.7559 17.5039V18.0225C14.7559 18.3301 14.8613 18.4268 15.1777 18.4268Z" fill="#555555"/> </svg>,
                                },
                            }}
                            formLabel={
                                'When did you last work with this person?'
                            }
                        />
                        <FormInputText_v2
                            control={control}
                            name={'data.workingRelationship'}
                            formLabel={'What was your working relationship?'}
                            textFieldProps={{
                                sx: UserRegSx,
                                placeholder: 'e.g. supervisor / colleague',
                                slotProps: {
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {/* prettier-ignore */}
                                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M18.5879 13.7422C20.7148 13.7422 22.4375 11.8701 22.4375 9.56738C22.4375 7.2998 20.7236 5.48926 18.5879 5.48926C16.4785 5.48926 14.7383 7.32617 14.7383 9.58496C14.7471 11.8789 16.4697 13.7422 18.5879 13.7422ZM8.40137 13.9443C10.2559 13.9443 11.75 12.3008 11.75 10.2705C11.75 8.29297 10.2559 6.69336 8.40137 6.69336C6.56445 6.69336 5.04395 8.31934 5.05273 10.2793C5.05273 12.3096 6.55566 13.9443 8.40137 13.9443ZM18.5879 12.2041C17.375 12.2041 16.3467 11.0615 16.3467 9.57617C16.3467 8.14355 17.3662 7.02734 18.5879 7.02734C19.8184 7.02734 20.8291 8.12598 20.8291 9.56738C20.8291 11.0439 19.8096 12.2041 18.5879 12.2041ZM8.40137 12.4238C7.4082 12.4238 6.56445 11.4746 6.56445 10.2793C6.56445 9.13672 7.39941 8.20508 8.40137 8.20508C9.4209 8.20508 10.2471 9.11914 10.2471 10.2705C10.2471 11.4746 9.40332 12.4238 8.40137 12.4238ZM3.56738 22.0215H10.3965C9.91309 21.7402 9.58789 21.0986 9.64941 20.5186H3.48828C3.32129 20.5186 3.25098 20.4395 3.25098 20.29C3.25098 18.2773 5.60645 16.3613 8.39258 16.3613C9.37695 16.3613 10.3613 16.6074 11.1172 17.0293C11.416 16.5986 11.7852 16.2295 12.2686 15.9131C11.1523 15.2275 9.76367 14.8584 8.39258 14.8584C4.6748 14.8584 1.66895 17.5127 1.66895 20.4307C1.66895 21.4854 2.30176 22.0215 3.56738 22.0215ZM13.1123 22.0215H24.0635C25.584 22.0215 26.3223 21.5381 26.3223 20.501C26.3223 18.084 23.2988 14.8672 18.5879 14.8672C13.877 14.8672 10.8535 18.084 10.8535 20.501C10.8535 21.5381 11.5918 22.0215 13.1123 22.0215ZM12.8311 20.4834C12.6289 20.4834 12.5498 20.4131 12.5498 20.2549C12.5498 18.9102 14.7207 16.4053 18.5879 16.4053C22.4551 16.4053 24.626 18.9102 24.626 20.2549C24.626 20.4131 24.5469 20.4834 24.3359 20.4834H12.8311Z" fill="#555555" /> </svg>
                                            </InputAdornment>
                                        ),
                                    },
                                },
                            }}
                        />

                        <Stack sx={{ gap: 1.5 }}>
                            <Typography>Reference details</Typography>
                            <FormInputText_v2
                                control={control}
                                name={'data.firstName'}
                                textFieldProps={{
                                    sx: UserRegSx,
                                    label: 'First Name:',
                                    placeholder: 'Enter here',
                                }}
                            />
                            <FormInputText_v2
                                control={control}
                                name={'data.lastName'}
                                textFieldProps={{
                                    sx: UserRegSx,
                                    label: 'Last Name:',
                                    placeholder: 'Enter here',
                                }}
                            />
                            <FormInputText_v2
                                control={control}
                                name={'data.email'}
                                textFieldProps={{
                                    sx: UserRegSx,
                                    label: 'Email:',
                                    placeholder: 'Enter here',
                                }}
                            />
                        </Stack>
                    </Stack>
                )
            }
        }
    }

    const workplaceUid = watch('data.workplaceUid')
    const onWorkplaceSelected = (id?: string) => {
        if (typeof id !== 'string') {
            resetField('data.firstName')
            resetField('data.lastName')
            resetField('data.email')
            return
        }
        if (id === defaultWorkplaceUid.current) {
            setValue('data.firstName', reference.data?.firstName || '')
            setValue('data.lastName', reference.data?.lastName || '')
            setValue('data.email', reference.data?.email || '')
            return
        }
        const entry = workingHistory.data?.entries?.[id]
        if (!entry) {
            logger.warn(`Could not find entry`)
            reset()
            return
        }

        const [fn, ln] = entry.supervisorName.split(' ')
        setValue('data.firstName', fn || '')
        setValue('data.lastName', ln || '')
        setValue('data.email', entry.supervisorEmail || '')
    }

    logger.debug(isValid)

    return (
        <>
            <RegModalWrapper
                title="Reference"
                handleUpdate={handleSubmit(onSubmit)}
                state={reference.data?.state}
                isAllComplete={isValid}
            >
                {hideForm ? (
                    <Stack sx={{ gap: 1.5 }} className={styles.modalContainer}>
                        <Stack
                            direction={'row'}
                            sx={{
                                gap: 1.5,
                                alignItems: 'center',
                            }}
                        >
                            {/* prettier-ignore */}
                            <svg width="60" height="60" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M47.97 77.9732C65.0559 77.9732 79.1586 63.8705 79.1586 46.8147C79.1586 29.7589 65.0258 15.6562 47.9399 15.6562C30.8841 15.6562 16.8115 29.7589 16.8115 46.8147C16.8115 63.8705 30.9142 77.9732 47.97 77.9732ZM47.97 51.9676C46.1921 51.9676 45.1676 50.9732 45.1073 49.1652L44.6854 34.1283C44.6251 32.26 45.951 30.9643 47.9399 30.9643C49.8684 30.9643 51.2847 32.2902 51.2245 34.1585L50.7423 49.1652C50.6821 51.0033 49.6876 51.9676 47.97 51.9676ZM47.97 62.394C45.9209 62.394 44.2334 60.9174 44.2334 58.9286C44.2334 56.9397 45.8908 55.4632 47.97 55.4632C50.0191 55.4632 51.6765 56.9397 51.6765 58.9286C51.6765 60.9475 49.989 62.394 47.97 62.394Z" fill="#FEC565"/> </svg>
                            <Box>
                                <Typography variant="h5" sx={{ mb: 0.5 }}>
                                    Reference - Working History Incomplete
                                </Typography>
                                <Typography sx={{ color: styles.neutrals500 }}>
                                    Complete your working history with children
                                    before nominating your reference.
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack sx={{ gap: 3 }} className={styles.modalContainer}>
                        <Stack className={styles.titleContainer}>
                            <Typography variant="h5">Reference</Typography>
                            <Typography className={styles.p}>
                                {['school', 'both'].includes(
                                    employmentSettingType || 'school'
                                )
                                    ? `Nominate a reference from your work history. Your reference must be a currently registered teacher in Australia who has worked with you before. Alternatively, you can request an online interview with our team.`
                                    : employmentSettingType === 'earlyChildcare'
                                      ? `Nominate a reference from your work history. Your reference must be either a currently registered teacher in Australia who has worked with you before or someone who holds a valid WWCC. Alternatively, you can request an online interview with our team.`
                                      : ''}
                            </Typography>
                        </Stack>
                        <Stack sx={{ gap: 3 }}>
                            {reference.status !== 'success' ||
                            workingHistory.status !== 'success' ? (
                                <LoadingSpinner />
                            ) : !!typeOptions && !hideForm ? (
                                <Controller
                                    control={control}
                                    name="type"
                                    render={(props) => (
                                        <FormControl
                                            error={Boolean(
                                                props.fieldState.error
                                            )}
                                            sx={{
                                                ...UserRegSx,
                                                backgroundColor:
                                                    styles.neutrals50,
                                            }}
                                        >
                                            <Select
                                                value={
                                                    encodeFormTypeValue(
                                                        props.field.value,
                                                        workplaceUid
                                                    ) || ''
                                                }
                                                displayEmpty={true}
                                                onChange={(e) => {
                                                    const { type, id } =
                                                        parseFormTypeValue(
                                                            e.target.value
                                                        )
                                                    props.field.onChange(type)
                                                    setValue(
                                                        'data.workplaceUid',
                                                        id,
                                                        {
                                                            shouldValidate: true,
                                                            shouldTouch: true,
                                                            shouldDirty: true,
                                                        }
                                                    )
                                                    onWorkplaceSelected(id)
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <Typography
                                                        color={
                                                            theme.palette
                                                                .neutrals[500]
                                                        }
                                                    >
                                                        Select reference
                                                    </Typography>
                                                </MenuItem>
                                                {typeOptions.map((el, i) => (
                                                    <MenuItem
                                                        key={`reftype_${i}`}
                                                        value={encodeFormTypeValue(
                                                            el.value,
                                                            el.id
                                                        )}
                                                    >
                                                        {el.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            ) : null}
                            {renderForm(formType)}
                        </Stack>
                    </Stack>
                )}
            </RegModalWrapper>
        </>
    )
}
const selectedHistoryValuePrefix = 'selectedHistory:'

const encodeFormTypeValue = (type: ReferenceFormTypes, id?: string): string => {
    switch (type) {
        case 'interview':
        case 'other': {
            return type
        }
        case 'selectedHistory': {
            return `${selectedHistoryValuePrefix}${id || ''}`
        }
    }
}

const parseFormTypeValue = (
    e: string
): { type: ReferenceFormTypes; id?: string } => {
    if (e === 'interview' || e === 'other') {
        return { type: e }
    }
    if (e.startsWith(selectedHistoryValuePrefix)) {
        return {
            type: 'selectedHistory',
            id: e.slice(selectedHistoryValuePrefix.length),
        }
    }
    throw new Error('Unknown reference form type selected')
}

export default Reference
